import { useLayoutEffect, useState } from 'react';

/** Previous fixed height — usable floor so short viewports still show options. */
export const FILTER_POPOVER_MIN_HEIGHT = 264;
/** Breathing room between the popover and the viewport edge. */
export const FILTER_POPOVER_VIEWPORT_GAP = 24;

type MeasureArgs = {
  triggerBottom: number;
  viewportHeight: number;
};

const asElement = (node: Element | null): HTMLElement | null =>
  node instanceof HTMLElement ? node : null;

/** The filter icon for the open column; antd 5's Dropdown marks it with `ant-dropdown-open`. */
export const getOpenFilterTrigger = (): HTMLElement | null =>
  asElement(document.querySelector('.ant-table-filter-trigger.ant-dropdown-open'));

/**
 * Max height for the filter popover.
 *
 * When there is room below the column, fill down to the viewport. When there
 * is not (short window / high zoom), keep at least {@link FILTER_POPOVER_MIN_HEIGHT}
 * so antd can flip the popover above the header instead of opening a sliver.
 *
 * Scrolling with the popover open can carry the trigger above the viewport top,
 * making `triggerBottom` negative; without the upper bound the popover would grow
 * taller than the window and push Select All / Reset / OK out of reach.
 */
export const getFilterPopoverMaxHeight = ({
  triggerBottom,
  viewportHeight,
}: MeasureArgs): number => {
  const availableBelow = Math.floor(viewportHeight - triggerBottom - FILTER_POPOVER_VIEWPORT_GAP);
  const viewportBound = Math.floor(viewportHeight - FILTER_POPOVER_VIEWPORT_GAP);

  return Math.max(FILTER_POPOVER_MIN_HEIGHT, Math.min(availableBelow, viewportBound));
};

export const measureFilterPopoverMaxHeight = (): number | null => {
  const trigger = getOpenFilterTrigger();
  if (!trigger) {
    return null;
  }

  return getFilterPopoverMaxHeight({
    triggerBottom: trigger.getBoundingClientRect().bottom,
    viewportHeight: window.innerHeight,
  });
};

type Params = {
  /** Whether the column filter dropdown is open (antd `visible` prop). */
  visible?: boolean;
};

/**
 * Max height for the checkbox filter popover.
 *
 * Only constrains the popover's own max-height and leaves placement to antd.
 * A popover that already fits below the trigger has no reason to flip; when
 * space below is shorter than the minimum, the minimum is used and antd
 * places it above the header (pre-MR behavior on short viewports).
 *
 * Measurement is driven by `visible` so it runs when the dropdown opens,
 * including for `filterDropdown: getFilterDropdown(...)` columns where the
 * hook lives on the always-mounted antd FilterDropdown.
 */
export const useFilterListMaxHeight = ({ visible = false }: Params = {}): number => {
  const [maxHeight, setMaxHeight] = useState(FILTER_POPOVER_MIN_HEIGHT);

  useLayoutEffect(() => {
    if (!visible) {
      return;
    }

    const measure = () => {
      const next = measureFilterPopoverMaxHeight();
      if (next === null) {
        return;
      }

      setMaxHeight(prev => (Math.abs(prev - next) <= 1 ? prev : next));
    };

    let frame: number | null = null;
    const scheduleMeasure = () => {
      if (frame !== null) {
        return;
      }

      frame = requestAnimationFrame(() => {
        frame = null;
        measure();
      });
    };

    // Measure immediately on open, then once more after the open class / portal settle.
    measure();
    scheduleMeasure();
    window.addEventListener('resize', scheduleMeasure);
    window.addEventListener('scroll', scheduleMeasure, { capture: true, passive: true });

    return () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
      window.removeEventListener('resize', scheduleMeasure);
      window.removeEventListener('scroll', scheduleMeasure, { capture: true });
    };
  }, [visible]);

  return maxHeight;
};
